import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { deleteProject } from "../../../api/projectLifecycleApi";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import Section from "../../../ui/forms/Section";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { useProjects } from "../../projectContext";

interface DangerZoneSectionProps {
  projectId: string;
  projectName: string;
  /** The installation's restore period, which the deletion starts. */
  restoreDays: number;
}

/**
 * The danger zone at the end of a project's settings (F121): deleting the project, with a red
 * button and its name typed once more, as GitHub deletes a repository. The page shows it only
 * where the server permits `delete_project`: to the project's administrators and to
 * administrators, never in the DMW project.
 */
export default function DangerZoneSection({ projectId, projectName, restoreDays }: DangerZoneSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reloadProjects } = useProjects();
  const [confirming, setConfirming] = useState(false);
  return (
    <Section title={t("projects.settings.danger.title")} className="app-card-danger">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="space-y-0.5">
          <p className="app-title font-semibold">{t("projects.settings.danger.delete")}</p>
          <p className="app-help-text text-xs">{t("projects.settings.danger.deleteHint", { count: restoreDays })}</p>
        </div>
        <AppButton variant="dangerSolid" size="sm" onClick={() => { setConfirming(true); }}>
          {t("projects.settings.danger.delete")}
        </AppButton>
      </div>
      {confirming ? (
        <ConfirmAction
          title={t("projects.settings.danger.confirmTitle", { project: projectName })}
          confirmLabel={t("projects.settings.danger.confirm")}
          tone="dangerSolid"
          typedConfirmation={projectName}
          onConfirm={async () => {
            await deleteProject(projectId, projectName);
            reloadProjects();
            void navigate(appPaths.projects());
          }}
          onClose={() => { setConfirming(false); }}
        >
          <p>{t("projects.settings.danger.confirmMessage", { count: restoreDays })}</p>
        </ConfirmAction>
      ) : null}
    </Section>
  );
}
